import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import constant from '../../constant';
import { X, Info, ArrowLeft, Send, Building, FileText, MessageSquare } from 'lucide-react';

const RaiseIssue = () => {
  const [formData, setFormData] = useState({
    organizationName: localStorage.getItem("hospitalName") || 'NA',
    issueSummary: '',
    issueDescription: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "issueSummary" && countWords(value) > 100) {
      setError("Issue summary cannot exceed 100 words");
      return;
    }
    if (name === "issueDescription" && countWords(value) > 250) {
      setError("Issue description cannot exceed 250 words");
      return;
    }

    setError(null);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let id = localStorage.getItem("hospitalId")

      const apiUrl = `${constant.REACT_APP_API_URL}/hospital/${id}/issue/`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          issue_summary: formData.issueSummary,
          issue_desc: formData.issueDescription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit issue');
      }

      setSubmitSuccess(true);
      setFormData({
        organizationName: localStorage.getItem("hospitalName") || 'NA',
        issueSummary: '',
        issueDescription: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } catch (err) {
      setError('Failed to submit issue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.organizationName.trim() &&
    formData.issueSummary.trim() &&
    formData.issueDescription.trim() &&
    countWords(formData.issueSummary) <= 100 &&
    countWords(formData.issueDescription) <= 250;

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <div className="px-6 pt-20 md:pt-4 pb-8 min-h-screen">
        <div className="max-w-8xl mx-auto lg:px-4 my-8">
          <div className=" overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-300">
              <div className="flex items-center space-x-4">
                <button onClick={handleBack} className="text-gray-500 hover:text-gray-700 transition-colors">
                  <ArrowLeft className="h-6 w-6" />
                </button>
                <div>
                  <h1 className="text-xl font-bold text-black">Raise an Issue</h1>
                  <p className="text-black-100">Submit your concern and we'll get back to you</p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <X className="h-4 w-4 text-red-600" />
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {submitSuccess && (
              <div className="mx-8 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="bg-green-100 rounded-full p-1">
                    <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-green-700 font-medium">Issue submitted successfully!</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Organization Name */}
              <div className="space-y-2">
                <label htmlFor="organizationName" className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                  <Building className="h-4 w-4 text-blue-600" />
                  <span>Organization Name</span>
                </label>
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleInputChange}
                  placeholder="Enter your organization name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                  disabled // Disabled since it's from localStorage
                  required
                />
              </div>

              {/* Issue Summary */}
              <div className="space-y-2">
                <label htmlFor="issueSummary" className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span>Issue Summary ({countWords(formData.issueSummary)}/100 words)</span>
                </label>
                <input
                  type="text"
                  id="issueSummary"
                  name="issueSummary"
                  value={formData.issueSummary}
                  onChange={handleInputChange}
                  placeholder="Brief summary of the issue"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                  required
                />
              </div>

              {/* Issue Description */}
              <div className="space-y-2">
                <label htmlFor="issueDescription" className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                  <span>Issue Description ({countWords(formData.issueDescription)}/250 words)</span>
                </label>
                <textarea
                  id="issueDescription"
                  name="issueDescription"
                  value={formData.issueDescription}
                  onChange={handleInputChange}
                  placeholder="Provide detailed description of the issue..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white resize-none"
                  required
                />
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-300">
                <div className="text-sm text-gray-500">
                  <Info className="h-4 w-4 inline mr-1" />
                  All fields are required
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={!isFormValid || isSubmitting}
                    className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center space-x-2 shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Submit Issue</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RaiseIssue;